import { BaseMiddleware } from '@makemydeal/dr-activities-common';
import { zipUtils, USPSCityStateZip } from '@makemydeal/dr-activities-common';
import { Selectors } from './selectors';
import { IShopper } from '@makemydeal/dr-platform-types';
import { IConfigLeadform, IHostActions } from '../types';
import { Actions } from './actions';
import {
    onFormSubmit as onFormSubmitAction,
    setLeadFormError,
    checkZipCodes,
    uspsZipSuccess,
    uspsZipFailure
} from '../store/actionCreators';

export class Middleware extends BaseMiddleware {
    constructor(
        public selector: Selectors,
        public config: IConfigLeadform,
        public hostActions?: IHostActions,
        public analyticsAction?: (store: any, key: string, desc: string) => void,
        public baseRoute?: string
    ) {
        super(selector, analyticsAction);
    }

    protected middlewareFunction = (store: any, _next: any, action: any) => {
        const state = store.getState();
        const presubmitAction = this.selector.getPreSubmitAction(state);
        const externalLeadSubmitFailureAction = this.selector.getExternalLeadSubmitFailureAction(state);

        switch (action.type) {
            case Actions.LEAD_FORM_ANALYTICS_ACTION:
                if (this.analyticsAction) {
                    this.analyticsAction(store, action.payload.key, action.payload.description);
                }
                break;
            case Actions.USPS_ZIP_REQUEST:
                {
                    const { zip, dealerZip } = action.payload;
                    const { apiUrl, userId } = this.config.usps;
                    const zipCodePromises: Promise<USPSCityStateZip>[] = [];
                    if (zip?.length === 5) {
                        zipCodePromises.push(zipUtils.getUspsZipCodeInfo(zip, { apiUrl, userId }));
                    }
                    if (dealerZip?.length === 5) {
                        zipCodePromises.push(zipUtils.getUspsZipCodeInfo(dealerZip, { apiUrl, userId }));
                    }

                    if (zipCodePromises.length > 0) {
                        Promise.all(zipCodePromises)
                            .then((arrayOfZipCodes: USPSCityStateZip[]) => {
                                store.dispatch(uspsZipSuccess(arrayOfZipCodes));
                            })
                            ['catch']((err) => {
                                console.error('Failed to load dealer zip or user zip', err);
                                store.dispatch(uspsZipFailure());
                            });
                    } else {
                        store.dispatch(uspsZipFailure());
                    }
                }
                break;

            case Actions.FETCH_QUESTIONS_OR_PAYMENT:
            case Actions.USPS_ZIP_INITIALIZE:
            case Actions.OPEN_LEAD_FORM_MODAL: {
                const { dealerSelectors, paymentSelectors } = this.selector.additionalSelectors;
                if (dealerSelectors.getCCPAEnabled(state) === true) {
                    const zip = paymentSelectors.getZipCode(state);
                    const dealerZip = dealerSelectors.getDealerZip(state);
                    store.dispatch(checkZipCodes(zip, dealerZip));
                }
                break;
            }
        }

        if (presubmitAction) {
            switch (action.type) {
                case Actions.LEAD_FORM_CONTINUE_SUBMISSION: {
                    const shopper: IShopper = this.selector.additionalSelectors.getShopperInfo(state);

                    store.dispatch(
                        this.hostActions?.updatedShopper({
                            shopper,
                            message: shopper.message || ''
                        })
                    );
                    store.dispatch(onFormSubmitAction());
                    store.dispatch(this.hostActions?.updateCommunicationPreference());
                    this.hostActions?.checkMessageOnFormSubmit(shopper, store.dispatch);
                    store.dispatch(this.hostActions?.checkZipOnFormSubmit(shopper.zip));
                    store.dispatch(this.hostActions?.submitOffer(true));
                    break;
                }
            }

            if (externalLeadSubmitFailureAction) {
                switch (action.type) {
                    case Actions.LEAD_FORM_SUBMISSION_FAILURE:
                    case Actions.OFFER_SUBMISSION_FAILURE: {
                        setLeadFormError('An error has occurred. Please review your information and try again.');
                        const extErrCount = this.selector.getExternalErrorCount(state);

                        // 1 retry allowed per business rule
                        if (extErrCount > 1) {
                            store.dispatch({ type: externalLeadSubmitFailureAction });
                        }
                    }
                }
            }
        }
    };
}
